import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown, Code2, CreditCard, ExternalLink, LayoutDashboard, ListOrdered, Settings, ShieldCheck, ShoppingBag, Wallet, Zap, TriangleAlert } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Tooltip } from '@base-ui/react/tooltip'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuGroup, DropdownMenuLabel } from '../ui/dropdown-menu'

interface SidebarProps {
  collapsed: boolean
  onNavigate: () => void
}
interface NavigationItem { key: string; label: string; icon?: LucideIcon; children?: NavigationItem[] }
const menuItems: NavigationItem[] = [
  { key: '/dashboard', label: '儀表盤', icon: LayoutDashboard },
  { key: '/dashboard/products', label: '產品管理', icon: ShoppingBag },
  { key: '/dashboard/orders', label: '訂單管理', icon: ListOrdered },
  { key: '/dashboard/disputes', label: '争议案件', icon: TriangleAlert },
  { key: '/dashboard/payment-methods', label: '支付方式', icon: CreditCard },
  { key: 'balance-group', label: '資金管理', icon: Wallet, children: [
    { key: '/dashboard/balance', label: '餘額概覽' },
    { key: '/dashboard/balance/transactions', label: '交易明細' },
    { key: '/dashboard/balance/refunds', label: '退款記錄' },
    { key: '/dashboard/payouts', label: '結款記錄' },
    { key: '/dashboard/bank-accounts', label: '銀行賬戶' },
  ] },
  { key: '/dashboard/kyb', label: 'KYB 認證', icon: ShieldCheck },
  { key: '/dashboard/kyc', label: '個人 KYC', icon: ShieldCheck },
  { key: 'developer-group', label: '開發者工具', icon: Code2, children: [
    { key: '/dashboard/developers/api-keys', label: 'API Keys' },
    { key: '/dashboard/developers/webhooks', label: 'Webhooks' },
    { key: 'https://docs.kyrenpay.com/', label: '文檔中心' },
  ] },
  { key: '/dashboard/settings', label: '設定', icon: Settings },
]
const destinations = menuItems.flatMap(item => item.children ?? [item]).map(item => item.key).filter(key => key.startsWith('/'))

export default function Sidebar({ collapsed, onNavigate }: SidebarProps) {
  const { pathname } = useLocation()
  const [openGroups, setOpenGroups] = useState(['balance-group', 'developer-group'])
  // Only the most specific matching route is current (balance overview is not current on transactions).
  const activePath = destinations.filter(path => pathname === path || (path !== '/dashboard' && pathname.startsWith(`${path}/`))).sort((a, b) => b.length - a.length)[0]
  useEffect(() => {
    const parent = menuItems.find(item => item.children?.some(child => child.key === activePath))
    if (parent) setOpenGroups(groups => groups.includes(parent.key) ? groups : [...groups, parent.key])
  }, [activePath])
  const link = (item: NavigationItem) => {
    const Icon = item.icon
    const className = `flex min-h-10 items-center ${collapsed ? 'justify-center' : 'gap-3'} rounded-lg px-3 py-2 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-primary ${activePath === item.key ? 'bg-primary/8 font-medium text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`
    if (item.key.startsWith('https:')) return <a href={item.key} target="_blank" rel="noopener noreferrer" className={className} onClick={onNavigate}>{item.label}<ExternalLink className="ml-auto size-3.5" aria-hidden="true" /></a>
    const destination = <Link to={item.key} aria-label={item.label} aria-current={activePath === item.key ? 'page' : undefined} className={className} onClick={onNavigate}>{Icon && <Icon className="size-4 shrink-0" aria-hidden="true" />}<span className={collapsed ? 'sr-only' : undefined}>{item.label}</span></Link>
    if (!collapsed) return destination
    return <Tooltip.Root>
      <Tooltip.Trigger render={destination} delay={150} />
      <Tooltip.Portal><Tooltip.Positioner side="right" sideOffset={12} className="z-50"><Tooltip.Popup role="tooltip" className="rounded-lg border border-border bg-white px-3 py-2 text-xs text-foreground shadow-md">{item.label}</Tooltip.Popup></Tooltip.Positioner></Tooltip.Portal>
    </Tooltip.Root>
  }
  return (
    <aside aria-label="商戶側邊欄" className={`flex h-full flex-col border-r border-border bg-white text-foreground ${collapsed ? 'w-20' : 'w-64'}`}>
      <Link to="/dashboard" aria-label="啟潤充值中心" onClick={onNavigate} className={`flex h-16 shrink-0 items-center border-b border-border font-semibold tracking-tight text-foreground ${collapsed ? 'justify-center' : 'gap-2.5 px-6'}`}>
        <span className="flex size-8 items-center justify-center rounded-lg bg-primary text-white"><Zap className="size-4" aria-hidden="true" /></span>
        {!collapsed && <span>啟潤充值中心<span className="mt-0.5 block text-[10px] font-normal tracking-widest text-muted-foreground">KYRENPAY</span></span>}
      </Link>
      <nav aria-label="商戶導覽" className="min-h-0 flex-1 overflow-y-auto p-3">
        {!collapsed && <p className="px-3 pb-2 pt-3 text-[10px] font-medium tracking-widest text-muted-foreground">商戶工作台</p>}
        <ul className="space-y-1">{menuItems.map(item => {
          if (!item.children) return <li key={item.key}>{link(item)}</li>
          const expanded = openGroups.includes(item.key)
          const Icon = item.icon!
          if (collapsed) {
            const active = item.children.some(child => child.key === activePath)
            return <li key={item.key}><DropdownMenu modal={false}>
              <DropdownMenuTrigger openOnHover delay={150} aria-label={item.label} data-active={active} className={`flex min-h-10 w-full items-center justify-center rounded-lg px-3 py-2 focus-visible:outline-2 focus-visible:outline-primary ${active ? 'bg-primary/8 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
                <Icon className="size-4 shrink-0" aria-hidden="true" />
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="start" sideOffset={12} className="min-w-44">
                <DropdownMenuGroup><DropdownMenuLabel>{item.label}</DropdownMenuLabel>
                  {item.children.map(child => <DropdownMenuItem key={child.key} aria-current={child.key === activePath ? 'page' : undefined} onClick={onNavigate}
                    render={child.key.startsWith('https:') ? <a href={child.key} target="_blank" rel="noopener noreferrer" /> : <Link to={child.key} />}
                    className={child.key === activePath ? 'bg-primary/8 text-primary' : undefined}>
                    {child.label}{child.key.startsWith('https:') && <ExternalLink className="ml-auto size-3.5" aria-hidden="true" />}
                  </DropdownMenuItem>)}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu></li>
          }
          return <li key={item.key}>
            <button type="button" aria-expanded={expanded} aria-controls={`nav-${item.key}`} className="flex min-h-10 w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary" onClick={() => setOpenGroups(groups => expanded ? groups.filter(key => key !== item.key) : [...groups, item.key])}>
              <Icon className="size-4 shrink-0" aria-hidden="true" /><span className="flex-1 text-left">{item.label}</span><ChevronDown aria-hidden="true" className={`size-3.5 transition-transform ${expanded ? '' : '-rotate-90'}`} />
            </button>
            <ul id={`nav-${item.key}`} hidden={!expanded} className="my-1 ml-5 space-y-1 border-l border-border pl-3">{item.children.map(child => <li key={child.key}>{link(child)}</li>)}</ul>
          </li>
        })}</ul>
      </nav>
      {!collapsed && <div className="border-t border-border px-6 py-4 text-xs text-muted-foreground">KyrenPay · 商戶控制台</div>}
    </aside>
  )
}
